import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../ui/dialog';
import { FileText, Download, CheckCircle, Camera, QrCode, Hash, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EspelhoPontoPanel = ({ empresas, empresaFiltro, funcionarios }) => {
  const { user } = useAuth();
  const [funcionarioId, setFuncionarioId] = useState('');
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [espelho, setEspelho] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAssinar, setShowAssinar] = useState(false);
  const [senha, setSenha] = useState('');
  const [selfieBase64, setSelfieBase64] = useState('');
  const [assinando, setAssinando] = useState(false);

  // Filtra funcionários ativos
  const funcFiltrados = (funcionarios || []).filter(
    f => f.situacao && f.situacao.toLowerCase() === 'ativo'
  );

  const gerarEspelho = async () => {
    if (!funcionarioId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/timetracking/gerar-espelho`, {
        funcionario_id: funcionarioId,
        periodo_inicio: `${ano}-${String(mes).padStart(2, '0')}-01`,
        periodo_fim: `${ano}-${String(mes).padStart(2, '0')}-${new Date(ano, mes, 0).getDate()}`,
        tipo_periodo: 'mensal'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEspelho(response.data?.espelho || response.data);
    } catch (err) {
      console.error('Erro ao gerar espelho:', err);
      alert('Erro ao gerar espelho de ponto.');
    } finally {
      setLoading(false);
    }
  };

  const baixarPDF = async () => {
    if (!espelho?.id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/timetracking/download-pdf-espelho/${espelho.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `espelho_ponto_${espelho.funcionario_nome?.replace(/\s+/g, '_')}_${mes}_${ano}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao baixar PDF:', err);
    }
  };

  const capturarSelfie = () => {
    if (typeof window === 'undefined') return;
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } })
      .then(stream => {
        video.srcObject = stream;
        video.play();
        setTimeout(() => {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          canvas.getContext('2d').drawImage(video, 0, 0);
          setSelfieBase64(canvas.toDataURL('image/jpeg').split(',')[1]);
          stream.getTracks().forEach(t => t.stop());
        }, 1000);
      })
      .catch(err => {
        console.error('Erro ao capturar selfie:', err);
        alert('Não foi possível acessar a câmera.');
      });
  };

  const assinarEspelho = async () => {
    if (!senha || !selfieBase64 || !espelho?.id) return;
    setAssinando(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/timetracking/assinar-espelho`, {
        espelho_id: espelho.id,
        senha,
        selfie_base64: selfieBase64
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEspelho(prev => ({ ...prev, assinado: true, data_assinatura: new Date().toISOString(), pdf_url: response.data.pdf_url }));
      setShowAssinar(false);
      setSenha('');
      setSelfieBase64('');
    } catch (err) {
      console.error('Erro ao assinar:', err);
      alert('Erro ao assinar espelho.');
    } finally {
      setAssinando(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Gerar Espelho de Ponto</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label className="text-sm">Funcionário</Label>
              <select
                value={funcionarioId}
                onChange={(e) => setFuncionarioId(e.target.value)}
                className="mt-1 block w-64 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none text-sm"
              >
                <option value="">Selecione...</option>
                {funcFiltrados.map(f => (
                  <option key={f.id} value={f.id}>{f.nome || f.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-sm">Mês</Label>
              <select
                value={mes}
                onChange={(e) => setMes(Number(e.target.value))}
                className="mt-1 block w-24 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{String(i + 1).padStart(2, '0')}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-sm">Ano</Label>
              <input
                type="number"
                value={ano}
                onChange={(e) => setAno(Number(e.target.value))}
                className="mt-1 block w-20 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none text-sm"
                min={2020}
                max={2030}
              />
            </div>
            <Button onClick={gerarEspelho} disabled={loading || !funcionarioId}>
              {loading ? 'Gerando...' : 'Gerar Espelho'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {espelho && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-base">Espelho de Ponto</CardTitle>
              {espelho.assinado && (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="h-3 w-3 mr-1" /> Assinado
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div><span className="text-gray-500">Funcionário:</span> {espelho.funcionario_nome}</div>
              <div><span className="text-gray-500">Período:</span> {espelho.periodo_inicio} a {espelho.periodo_fim}</div>
            </div>

            {espelho.totalizadores && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded p-3 text-center">
                  <p className="text-xs text-gray-500">Trabalhadas</p>
                  <p className="text-lg font-bold">{espelho.totalizadores.total_horas_trabalhadas || '00:00'}</p>
                </div>
                <div className="bg-gray-50 rounded p-3 text-center">
                  <p className="text-xs text-gray-500">Extras</p>
                  <p className="text-lg font-bold text-green-600">{espelho.totalizadores.total_horas_extras_diurnas || '00:00'}</p>
                </div>
                <div className="bg-gray-50 rounded p-3 text-center">
                  <p className="text-xs text-gray-500">Atrasos</p>
                  <p className="text-lg font-bold text-amber-600">{espelho.totalizadores.total_atrasos || '00:00'}</p>
                </div>
                <div className="bg-gray-50 rounded p-3 text-center">
                  <p className="text-xs text-gray-500">Saldo Acumulado</p>
                  <p className="text-lg font-bold">{espelho.totalizadores.saldo_acumulado_banco_horas || '00:00'}</p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              {!espelho.assinado && (
                <Button onClick={() => setShowAssinar(true)}>
                  <Camera className="h-4 w-4 mr-1" /> Assinar Eletronicamente
                </Button>
              )}
              {espelho.assinado && espelho.pdf_url && (
                <Button onClick={baixarPDF} variant="outline">
                  <Download className="h-4 w-4 mr-1" /> Baixar PDF Assinado
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={showAssinar} onOpenChange={setShowAssinar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assinar Espelho de Ponto</DialogTitle>
            <DialogDescription>
              Capture sua selfie e informe sua senha para assinar eletronicamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Selfie</Label>
              <div className="mt-1">
                {selfieBase64 ? (
                  <img
                    src={`data:image/jpeg;base64,${selfieBase64}`}
                    alt="Selfie"
                    className="w-48 h-48 object-cover rounded-lg border"
                  />
                ) : null}
                <Button onClick={capturarSelfie} variant="outline" className="mt-2" type="button">
                  <Camera className="h-4 w-4 mr-1" /> Capturar Selfie
                </Button>
              </div>
            </div>
            <div>
              <Label>Senha</Label>
              <Input
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                placeholder="Informe sua senha"
              />
            </div>
            <Button
              onClick={assinarEspelho}
              disabled={assinando || !senha || !selfieBase64}
              className="w-full"
            >
              {assinando ? 'Assinando...' : 'Confirmar Assinatura'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EspelhoPontoPanel;
